// Cửa sổ Động Phủ: gieo hạt, công trình (nâng cấp), Luyện Đan (đan phương, lò, phẩm đan, đan độc).
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { remainingText } from '@i18n/time';
import { HERBS, RECIPES, recipeDef, type BuildingId } from '@data/crafting';
import { alchemyXpNeed, buildingCap, furnaceCount, plotMatureAtMs, upgradeCost } from '@sim/crafting';
import { stepInfo } from '@sim/cultivation';
import {
  cave,
  caveGrowth,
  cancelCraft,
  craftBlock,
  ingredientCount,
  plant,
  startCraft,
  startUpgrade,
  upgradeBlock,
} from '@state/modules/cave';
import { cultivation } from '@state/modules/cultivation';
import { inventory } from '@state/modules/inventory';
import { closeWindows } from '@state/modules/ui';
import { wallet } from '@state/modules/wallet';
import { TextButton } from '../../kit/TextButton';
import { NumBar } from '../../kit/NumBar';
import { useClock } from '../../useClock';

export function PlotWindow({ index }: { index: number }): JSX.Element {
  const p = cave.value.plots[index];
  const goods = inventory.value.goods;
  const clock = useClock();
  if (!p) return <p class="ht-block">{t('ui.page.empty')}</p>;
  if (p.herbId)
    return (
      <>
        <p class="ht-block">{t(`${p.herbId}.desc`)}</p>
        <p class="ht-reason dim">
          {t('ui.cave.growing', { left: remainingText(plotMatureAtMs(p, caveGrowth.value) - clock) })}
        </p>
      </>
    );
  return (
    <>
      <p class="ht-block">{t('ui.cave.plantBody')}</p>
      <div class="ht-rows">
        {HERBS.map((h) => {
          const n = goods[h.seedId] ?? 0;
          return (
            <div class="ht-statline" key={h.id}>
              <span>
                <b class="row-name">{t(`${h.seedId}.name`)}</b>
                <small>{t('ui.cave.seedInfo', { h: formatFull(h.matureH), y: h.yield })}</small>
              </span>
              <span class="ht-chip">×{n}</span>
              <TextButton
                id={`plant-${h.seedId}`}
                disabled={n < 1}
                onClick={() => plant(index, h.seedId) && closeWindows()}
              >
                {t('ui.cave.plant')}
              </TextButton>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function BuildingWindow({ id }: { id: BuildingId }): JSX.Element {
  const b = cave.value.buildings[id];
  const clock = useClock();
  const r = stepInfo(cultivation.value.step).realmIdx;
  const cap = buildingCap(id, r);
  const cost = b.lv < 10 ? upgradeCost(id, b.lv + 1) : null;
  const block = upgradeBlock(id);
  const have = inventory.value.goods[cost?.material.id ?? ''] ?? 0;
  return (
    <>
      <p class="ht-block">{t(`craft.building.${id}.desc`)}</p>
      <div class="ht-statline">
        <span>{t('ui.cave.level')}</span>
        <b>{t('ui.cave.lvCap', { n: b.lv, cap })}</b>
      </div>
      {cost && (
        <div class="ht-statline">
          <span>{t('ui.cave.nextCost')}</span>
          <b>
            {t('ui.cave.cost', {
              st: formatFull(cost.stones),
              mat: t(`${cost.material.id}.name`),
              n: cost.material.count,
              have,
              m: formatFull(Math.round(cost.minutes)),
            })}
          </b>
        </div>
      )}
      <div class="ht-actions">
        <TextButton id={`upgrade-${id}`} disabled={block !== null} onClick={() => startUpgrade(id)}>
          {b.upgrade ? remainingText(b.upgrade.endMs - clock) : t('ui.cave.upgrade')}
        </TextButton>
        <span class="ht-chip price wallet" style={{ '--cur-rgb': 'var(--cur-spiritStone-rgb)' }}>
          {formatFull(wallet.value.spiritStone)}
        </span>
      </div>
      {block && !b.upgrade && <p class="ht-reason">{t(`ui.cave.block.${block}`)}</p>}
    </>
  );
}

export function AlchemyWindow(): JSX.Element {
  const c = cave.value;
  // Đọc để vẽ lại khi túi đổi (số nguyên liệu, lý do chặn).
  void inventory.value;
  const tox = cultivation.value.pillToxicity;
  const clock = useClock();
  const [cancelMsg, setCancelMsg] = useState(false);
  const known = RECIPES.filter((r) => c.alchemy.knownRecipes.includes(r.id));
  // Nút câm luôn kèm lý do (UI_SPEC §3.4): lý do của đan phương đầu tiên bị chặn, hoặc hủy trễ.
  const block = known.map((r) => craftBlock(r.id)).find((b) => b !== null);
  const reason = cancelMsg ? t('ui.alchemy.cancelStarted') : block ? t(`ui.alchemy.block.${block}`) : null;
  return (
    <>
      <NumBar
        id="alchemy-xp"
        value={c.alchemy.xp}
        max={alchemyXpNeed(c.alchemy.lv)}
        ariaLabel={t('ui.alchemy.skill', { n: c.alchemy.lv })}
      />
      <small class="zone-note">{t('ui.alchemy.skill', { n: c.alchemy.lv })}</small>
      <div class="ht-rows" data-id="alchemy-recipes">
        {known.length === 0 && <p class="ht-reason dim">{t('ui.alchemy.noRecipe')}</p>}
        {known.map((r) => {
          const block = craftBlock(r.id);
          return (
            <div class="ht-statline" key={r.id}>
              <span>
                <b class="row-name">{t(`${r.outputId}.name`)}</b>
                <small>
                  {r.ingredients
                    .map((i) => `${t(`${i.id}.name`)} ${ingredientCount(i.id)}/${i.count}`)
                    .join(' · ')}
                </small>
              </span>
              <TextButton
                id={`craft-${r.id}`}
                disabled={block !== null}
                onClick={() => {
                  setCancelMsg(false);
                  startCraft(r.id);
                }}
              >
                {t('ui.alchemy.craft', { m: r.craftMin })}
              </TextButton>
            </div>
          );
        })}
      </div>
      <div class="ht-rows" data-id="alchemy-jobs">
        {c.jobs.map((j) => (
          <div class="ht-statline" key={j.id}>
            <span>{t(`${recipeDef(j.recipeId).outputId}.name`)}</span>
            <span class="ht-chip">
              {j.startMs > clock ? t('ui.alchemy.queued') : remainingText(j.endMs - clock)}
            </span>
            {j.startMs > clock && (
              <TextButton
                variant="ghost"
                id={`craft-cancel-${j.id}`}
                onClick={() => setCancelMsg(!cancelCraft(j.id))}
              >
                {t('ui.cancel')}
              </TextButton>
            )}
          </div>
        ))}
      </div>
      <p class={reason ? 'ht-reason' : 'ht-reason dim'} data-id="alchemy-reason">
        {reason && `${reason} `}
        {t('ui.alchemy.footer', {
          f: furnaceCount(c.buildings),
          tox: Math.round(tox),
          pattern: t('ui.alchemy.patternKnown'),
        })}
      </p>
    </>
  );
}
